/**
 * Liste des paiements d'un chantier avec actions
 */

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Pencil, Trash2, FileText, ExternalLink, ChevronDown } from 'lucide-react';
import { formatMontant, formatDate } from '@/lib/formatters';
import { sanitizeUrl } from '@/lib/security/urlSanitizer';
import type { PaiementClient, FactureClientSimple } from '@/types/paiement';
import {
  LABELS_TYPE_PAIEMENT,
  LABELS_MOYEN_PAIEMENT,
  LABELS_STATUT_PAIEMENT_ITEM,
} from '@/types/paiement';


interface PaiementsListProps {
  paiements: PaiementClient[];
  facturesClients: FactureClientSimple[];
  onAdd: () => void;
  onEdit: (paiement: PaiementClient) => void;
  onDelete: (id: string) => Promise<boolean>;
  onAddAvenant?: () => void;
  onAddRemise?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}

export function PaiementsList({
  paiements,
  facturesClients,
  onAdd,
  onEdit,
  onDelete,
  onAddAvenant,
  onAddRemise,
  isLoading = false,
  isDisabled = false
}: PaiementsListProps) {
  // Trouver la facture liée
  const getFacture = (factureId: string | undefined): FactureClientSimple | undefined => {
    if (!factureId) return undefined;
    return facturesClients.find(f => f.id === factureId);
  };

  // Supprimer un paiement
  const handleDelete = async (paiement: PaiementClient) => {
    if (!confirm(`Supprimer ce paiement de ${formatMontant(paiement.montant)} ?`)) return;
    await onDelete(paiement.id);
  };

  // Couleur du badge statut
  const getStatutBadgeVariant = (statut: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (statut) {
      case 'encaisse': return 'default';
      case 'en_attente': return 'outline';
      case 'annule': return 'destructive';
      default: return 'secondary';
    }
  };

  // Skeleton pendant le chargement
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-6 bg-muted rounded w-48 animate-pulse" />
          <div className="h-9 bg-muted rounded w-32 animate-pulse" />
        </div>
        <div className="border rounded-md">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4 p-4 border-b animate-pulse">
              <div className="h-4 bg-muted rounded w-24" />
              <div className="h-4 bg-muted rounded w-20" />
              <div className="h-4 bg-muted rounded w-16" />
              <div className="h-4 bg-muted rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <FileText className="h-5 w-5 text-muted-foreground" />
          Paiements enregistrés ({paiements.length})
        </h3>
        {!isDisabled && (
          <div className="flex items-center gap-2">
            {/* Dropdown avenant / remise */}
            {(onAddAvenant || onAddRemise) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="bg-white text-black font-semibold hover:bg-amber-400 hover:text-black hover:border-amber-400">
                    <Plus className="h-4 w-4 mr-1" />
                    Ajustement
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {onAddAvenant && (
                    <DropdownMenuItem onClick={onAddAvenant}>
                      Ajouter un avenant
                    </DropdownMenuItem>
                  )}
                  {onAddRemise && (
                    <DropdownMenuItem onClick={onAddRemise}>
                      Ajouter une remise
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button onClick={onAdd} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Ajouter un paiement
            </Button>
          </div>
        )}
      </div>

      {/* Tableau ou message vide */}
      {paiements.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-border rounded-lg">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground mb-4">Aucun paiement enregistré</p>
          {!isDisabled && (
            <Button onClick={onAdd} variant="outline">
              <Plus className="h-4 w-4 mr-1" />
              Enregistrer un paiement
            </Button>
          )}
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Moyen</TableHead>
                <TableHead>Référence</TableHead>
                <TableHead>Facture liée</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paiements.map((paiement) => {
                const facture = getFacture(paiement.facture_client_id);
                return (
                  <TableRow key={paiement.id}>
                    <TableCell className="font-medium">
                      {formatDate(paiement.date_paiement)}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-green-500">
                      {formatMontant(paiement.montant)}
                    </TableCell>
                    <TableCell>
                      {LABELS_TYPE_PAIEMENT[paiement.type]}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {paiement.moyen_paiement
                        ? LABELS_MOYEN_PAIEMENT[paiement.moyen_paiement]
                        : '-'
                      }
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {paiement.reference || '-'}
                    </TableCell>
                    <TableCell>
                      {facture ? (
                        <span className="text-sm text-blue-500">
                          {facture.numero_facture}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatutBadgeVariant(paiement.statut)}>
                        {LABELS_STATUT_PAIEMENT_ITEM[paiement.statut]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {!isDisabled && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onEdit(paiement)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            {paiement.fichier_url && (
                              <DropdownMenuItem
                                onClick={() => window.open(sanitizeUrl(paiement.fichier_url!), '_blank')}
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Voir justificatif
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleDelete(paiement)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default PaiementsList;
